import { AssignationType } from "@models/ISchema";
import { create } from "zustand";
import { ILeague } from "./ContextVacancies";
import { IErrorWarning } from "@models/Errors/ErrorWarning";
import { InstructorLinkTags } from "@modules/vacancies/Instructor/instructorLinkTags/types";

type AssignationTypeData = AssignationType & {
  courseType: string;
};

export type AssignationTypeError = {
  code: string;
  link: ILeague;
  maxAllowed: number;
  error: IErrorWarning & { messageAccent?: string };
};

export type InstructorLinkTagsChangeset = {
  instructorLinkId: string;
  linkId: string;
  tags: InstructorLinkTags;
};

/** An error rendered in the error card of a link. */
export type LinkError = {
  link: ILeague;
  error: IErrorWarning & { messageAccent?: string };
};

export type InstructorLinkTagsError = LinkError;

export type InstructorCorrectionValidity = {
  responsibilityValid: boolean;
  primaryValid: boolean;
  primaryCount: number;
  existingRulesValid: boolean;
  valid: boolean;
};

type State = {
  assignationTypesToSave: {
    data: AssignationTypeData;
    instructorId: string;
    instructorLinkId: string;
    linkId: string;
  }[];
  // Every link of the Docentes tab mounts its own instructors table, so the
  // per-link state below is keyed by linkId: a link must never overwrite the
  // errors, changes or validity of another one.
  assignationTypesErrorsByLink: Record<string, AssignationTypeError[]>;
  assignationHaveChangesByLink: Record<string, boolean>;
  instructorLinkValidityByLink: Record<string, boolean>;
  /** Save gate of the Vacancies tab (`Groups.tsx`); the Docentes tab uses `instructorLinkValidityByLink`. */
  allChangesAreValid: boolean;
  instructorLinkTagsToSave: InstructorLinkTagsChangeset[];
  instructorLinkTagsErrors: InstructorLinkTagsError[];
  instructorCorrectionValidityByLink: Record<string, InstructorCorrectionValidity>;
};

export const useVacanciesStore = create<State>(() => ({
  assignationTypesToSave: [],
  assignationTypesErrorsByLink: {},
  assignationHaveChangesByLink: {},
  instructorLinkValidityByLink: {},
  allChangesAreValid: false,
  instructorLinkTagsToSave: [],
  instructorLinkTagsErrors: [],
  instructorCorrectionValidityByLink: {},
}));

/** Returns a copy of `map` with the link entry set, or removed when `value` is null. */
const setLinkEntry = <T>(map: Record<string, T>, linkId: string, value: T | null) => {
  const next = { ...map };
  if (value === null) delete next[linkId];
  else next[linkId] = value;
  return next;
};

export const setInstructorCorrectionValidity = (
  linkId: string,
  validity: InstructorCorrectionValidity | null,
) => {
  useVacanciesStore.setState(state => {
    const next = { ...state.instructorCorrectionValidityByLink };
    if (validity) next[linkId] = validity;
    else delete next[linkId];
    return { instructorCorrectionValidityByLink: next };
  });
};

export const setInstructorLinkTagsToSave = (changesets: InstructorLinkTagsChangeset[]) => {
  useVacanciesStore.setState({ instructorLinkTagsToSave: changesets });
};

export const setInstructorLinkTagsErrors = (errors: InstructorLinkTagsError[]) => {
  useVacanciesStore.setState({ instructorLinkTagsErrors: errors });
};

export const setAssignationTypesToSave = (assignationTypes: State["assignationTypesToSave"]) => {
  useVacanciesStore.setState({ assignationTypesToSave: assignationTypes });
};

export const setAllChangesAreValid = (isValid: boolean) => {
  useVacanciesStore.setState({ allChangesAreValid: isValid });
};

export const setAssignationTypesErrorsForLink = (
  linkId: string,
  errors: AssignationTypeError[] | null,
) => {
  useVacanciesStore.setState(state => ({
    assignationTypesErrorsByLink: setLinkEntry(
      state.assignationTypesErrorsByLink,
      linkId,
      errors?.length ? errors : null,
    ),
  }));
};

export const setAssignationHaveChangesForLink = (linkId: string, haveChanges: boolean | null) => {
  useVacanciesStore.setState(state => ({
    assignationHaveChangesByLink: setLinkEntry(
      state.assignationHaveChangesByLink,
      linkId,
      haveChanges,
    ),
  }));
};

export const setInstructorLinkValidity = (linkId: string, isValid: boolean | null) => {
  useVacanciesStore.setState(state => ({
    instructorLinkValidityByLink: setLinkEntry(state.instructorLinkValidityByLink, linkId, isValid),
  }));
};
